/* ============================================================================
   APEKX UI — interaction layer
   ----------------------------------------------------------------------------
   No dependencies. Deferred. Every feature is independently guarded so one
   failure cannot take down the rest of the page.

   Design notes worth keeping:
   - Pointer handlers are rAF-coalesced. A raw pointermove listener that writes
     style on every event fires ~120x/sec on a 120Hz display and is the single
     most common cause of "why is this site janky".
   - Reveal elements are visible by default in CSS; the hiding class is only
     added when this script runs. If the script 404s you get a plain page, not
     a blank one.
   - Everything checks prefers-reduced-motion at runtime, not just in CSS,
     so JS-driven effects (scramble, magnetic) genuinely stop.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var canHover = window.matchMedia('(hover: hover)').matches;

  /* ---------------------------------------------------------------- reveal */
  function initReveal() {
    var els = document.querySelectorAll('[data-apekx-reveal]');
    if (!els.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('apekx-in'); });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var delay = parseInt(el.getAttribute('data-apekx-delay') || '0', 10);
          setTimeout(function () { el.classList.add('apekx-in'); }, delay);
          io.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.1 }
    );

    els.forEach(function (el) { io.observe(el); });
  }

  /* -------------------------------------------------------------- magnetic */
  function initMagnetic() {
    if (reduced || !canHover) return;
    var els = document.querySelectorAll('.apekx-magnetic, .button--primary');
    if (!els.length) return;

    els.forEach(function (el) {
      var frame = null;
      var strength = parseFloat(el.getAttribute('data-apekx-strength') || '0.28');

      function onMove(e) {
        if (frame) return;                       // coalesce to one write per frame
        frame = requestAnimationFrame(function () {
          frame = null;
          var r = el.getBoundingClientRect();
          var dx = e.clientX - (r.left + r.width / 2);
          var dy = e.clientY - (r.top + r.height / 2);
          // Cap displacement so large buttons don't drift absurdly far.
          var max = 14;
          el.style.setProperty('--mx', Math.max(-max, Math.min(max, dx * strength)) + 'px');
          el.style.setProperty('--my', Math.max(-max, Math.min(max, dy * strength)) + 'px');
        });
      }

      function reset() {
        if (frame) { cancelAnimationFrame(frame); frame = null; }
        el.style.setProperty('--mx', '0px');
        el.style.setProperty('--my', '0px');
      }

      el.classList.add('apekx-magnetic');
      el.addEventListener('pointermove', onMove, { passive: true });
      el.addEventListener('pointerleave', reset, { passive: true });
      el.addEventListener('blur', reset, { passive: true });
    });
  }

  /* -------------------------------------------------------------- scramble */
  var GLYPHS = '█▓▒░<>/\\[]{}*#%@ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

  function scramble(el) {
    if (reduced) return;
    if (el.dataset.apekxBusy === '1') return;

    var target = el.dataset.apekxText || el.textContent;
    el.dataset.apekxText = target;
    el.dataset.apekxBusy = '1';
    el.classList.add('is-scrambling');

    var frame = 0;
    var total = 18;
    var timer = setInterval(function () {
      var out = '';
      for (var i = 0; i < target.length; i++) {
        var ch = target[i];
        if (ch === ' ') { out += ' '; continue; }
        // Characters "lock in" progressively left-to-right.
        if (i < (frame / total) * target.length) out += ch;
        else out += GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
      }
      el.textContent = out;
      frame++;
      if (frame > total) {
        clearInterval(timer);
        el.textContent = target;
        el.classList.remove('is-scrambling');
        el.dataset.apekxBusy = '0';
      }
    }, 28);
  }

  function initScramble() {
    if (reduced || !canHover) return;
    var els = document.querySelectorAll('.apekx-scramble');
    els.forEach(function (el) {
      el.addEventListener('mouseenter', function () { scramble(el); }, { passive: true });
    });
  }

  /* ------------------------------------------------------------ sticky ATC */
  function initStickyAtc() {
    var bar = document.querySelector('[data-apekx-sticky-atc]');
    if (!bar) return;

    // Anchor on the real product form's submit button.
    var anchor = document.querySelector('product-form .product-form__submit')
              || document.querySelector('.product-form__submit');
    if (!anchor || !('IntersectionObserver' in window)) return;

    var proxy = bar.querySelector('[data-apekx-atc-proxy]');
    if (proxy) {
      proxy.addEventListener('click', function (e) {
        e.preventDefault();
        // Delegate to the real button so Dawn's cart logic and variant state apply.
        anchor.click();
      });
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { threshold: 0 }
    );
    io.observe(anchor);
  }

  /* ------------------------------------------------------------------ boot */
  function boot() {
    try { initReveal(); }     catch (e) { console.warn('[apekx] reveal', e); }
    try { initMagnetic(); }   catch (e) { console.warn('[apekx] magnetic', e); }
    try { initScramble(); }   catch (e) { console.warn('[apekx] scramble', e); }
    try { initStickyAtc(); }  catch (e) { console.warn('[apekx] sticky-atc', e); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Sections re-render in the theme editor; re-init so effects survive edits.
  document.addEventListener('shopify:section:load', boot);
})();
